import { Component, HostListener, OnInit, ViewChild } from '@angular/core';
import { OrderFlowService } from '../order-flow.service';
import { OrderFlowProductChoiceComponent } from '../order-flow-product-choice/order-flow-product-choice.component';
import { OrderFlowOrderDetailComponent } from '../order-flow-order-detail/order-flow-order-detail.component';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { OrderFlowMeasureComponent } from '../order-flow-measure/order-flow-measure.component';
import { ActivatedRoute, Params, Router } from '@angular/router';
import {
  MeasurementContainer,
  FilledAccessory,
  Accessory,
} from '../../../model/attribute';
import { OrderFlowOrderSummaryComponent } from '../order-flow-order-summary/order-flow-order-summary.component';
import { AuthService } from '../../../core/auth/auth.service';

@Component({
  selector: 'app-order-flow-container',
  templateUrl: './order-flow-container.component.html',
  styleUrls: ['./order-flow-container.component.css'],
  standalone: true,
  imports: [
    OrderFlowProductChoiceComponent,
    OrderFlowOrderDetailComponent,
    OrderFlowMeasureComponent,
    OrderFlowOrderSummaryComponent,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
  ],
})
export class OrderFlowContainerComponent implements OnInit {
  @ViewChild(OrderFlowProductChoiceComponent)
  productChoiceComponent!: OrderFlowProductChoiceComponent;

  @ViewChild(OrderFlowOrderDetailComponent)
  orderDetailComponent!: OrderFlowOrderDetailComponent;

  @ViewChild(OrderFlowMeasureComponent)
  orderFlowMeasureComponent!: OrderFlowMeasureComponent;

  @ViewChild(OrderFlowOrderSummaryComponent)
  orderFlowOrderSummaryComponent!: OrderFlowOrderSummaryComponent;

  bootstrap: any;
  currentStep = 1;
  isLoading = false;
  isQuote = false;
  selectedQuoteType: 'dealer' | 'msrp' | null = null;
  quoteOrderNumber: any;
  isLoadingChange = false;
  showSummary = false;
  orderDetailSummary: any;
  submitPayload: any;
  filledMeasurements: MeasurementContainer[] = [];
  filledAccessories: FilledAccessory[] = [];
  attachments: any[] = [];

  isDuplicateAction = false;
  baseOrderNumber = '';
  savedOrderNumber = '';
  orderNumberParam = '';
  comments: string | null = null;
  btnSaveLabel = 'Save';
  hideBtnCancel = false;
  hideBtnSubmit = false;
  editfromadmin = false;
  productNameSelected = '';
  orderStatus: string | null = '';
  countryCode = 'en-US';
  private hasUnsavedChanges = false;
  private isNavigatingAway = false;


  constructor(
    private orderFlowService: OrderFlowService,
    private router: Router,
    private route: ActivatedRoute,
    private authService: AuthService,
  ) {}

  get isTherapist(): boolean { return this.authService.isTherapist(); }
  get therapistCanSubmit(): boolean { return this.authService.allowToSubmitOrders(); }

  /**
   * Gera um GUID/UUID único no formato padrão
   * @returns string com formato: xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx
   */
  private generateGuid(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  /**
   * Garante que todos os atributos dos accessories tenham GUID
   * Essencial para compatibilidade entre criação e edição de pedidos
   * @param accessories Array de FilledAccessory que pode ou não ter GUIDs nos atributos
   */
  private ensureAttributeGuids(accessories: FilledAccessory[]): void {
    if (!accessories || accessories.length === 0) {
      return;
    }

    accessories.forEach(filledAccessory => {
      // Garante GUIDs nos atributos do accessory principal
      if (filledAccessory.accessory?.attributes) {
        filledAccessory.accessory.attributes.forEach((attribute: any) => {
          if (!attribute.guid) {
            attribute.guid = this.generateGuid();
            console.log(`Generated GUID for attribute ${attribute.name}: ${attribute.guid}`);
          }
        });
      }

      // Garante GUIDs nos selectedAttributes (se existirem)
      if (filledAccessory.selectedAttributes) {
        filledAccessory.selectedAttributes.forEach((attribute: any) => {
          if (!attribute.guid) {
            attribute.guid = this.generateGuid();
            console.log(`Generated GUID for selected attribute ${attribute.name}: ${attribute.guid}`);
          }
        });
      }
    });
  }

@HostListener('window:popstate', ['$event'])
onPopState(event: PopStateEvent): void {
  if (this.hasUnsavedChanges && !this.isNavigatingAway) {
    
    history.pushState(null, '', location.href);
    
    const confirmed = confirm('You have unsaved data. Are you sure you want to leave? All progress will be lost.');
    
    if (confirmed) {
      this.isNavigatingAway = true;
      this.hasUnsavedChanges = false;
      window.location.href = '/en-us/orders';
    }
  }
}

  ngOnInit(): void {
    this.hasUnsavedChanges = true;
    
    history.pushState(null, '', location.href);
    
    setTimeout(() => {
      if (this.hasUnsavedChanges && !this.isNavigatingAway) {
        history.pushState(null, '', location.href);
      }
    }, 100);

    // countryCode is carried by the parent (:countryCode) route segment; keep it so post-save
    // navigation stays in the user's country instead of defaulting to en-US.
    const parentCountryCode = this.route.parent?.snapshot.paramMap.get('countryCode');
    if (parentCountryCode) {
      this.countryCode = parentCountryCode;
    }

    this.route.params.subscribe((params: Params) => {
      const action = params['action'];

      this.route.queryParams.subscribe((queryParams: Params) => {
        this.orderStatus = queryParams['status'];
        if((this.orderStatus === 'Submitted' || this.orderStatus === 'Quote') && action === 'edit') {
          this.orderStatus = 'SavePurchaseOrder';
        }
      });

      this.orderNumberParam = params['orderNumber'];

      if (action === 'duplicate' && this.orderNumberParam) {
        this.isDuplicateAction = true;
        this.baseOrderNumber = this.orderNumberParam;

        this.getOrderToEdit();
      }

      if (action === 'edit') {
        this.isDuplicateAction = true;
        this.savedOrderNumber = this.orderNumberParam;
        this.getOrderToEdit();
      }

      if (action === 'editfromadmin') {
        this.editfromadmin = true;
        this.savedOrderNumber = this.orderNumberParam;
        const accessToken = params['token'];
        this.authService.setAccessToken(accessToken);
        // Admin-initiated edits never run as a therapist; clear any stale flag from a previous
        // session so Submit performs a real submit rather than ReadyToSubmit.
        this.authService.setUserContext(false, true);
        this.btnSaveLabel = 'Save';
        this.hideBtnCancel = true;
        this.hideBtnSubmit = this.orderStatus === 'Submitted' ? true : false;
        this.getOrderToEdit();
      }
    });
  }

private clearUnsavedChanges(): void {
  this.hasUnsavedChanges = false;
  this.isNavigatingAway = true;
}

ngOnDestroy(): void {
  this.hasUnsavedChanges = false;
  this.isNavigatingAway = true;
}

  private getOrderToEdit() {
    this.isLoading = true;
    this.orderFlowService.getOrderToEdit(this.orderNumberParam).subscribe(
      (response) => {
        this.isLoading = false;
        this.handleGetOrderToEditResponse(response);
      },
      (error) => {
        console.error('Error adding products to order:', error);
        this.isLoading = false;
        alert('Error adding products to order.');
      },
    );
  }
  
private resolveParentOrigin(): string {
    const ALLOWED_PARENTS = [
      'http://localhost:4200',
      'https://sig-mtm-we-webapp-adm-app-uat.azurewebsites.net', 
      'https://sig-mtm-we-webapp-adm-app-prd.azurewebsites.net', 
    ];

    try {
      if (document.referrer) {
        const refOrigin = new URL(document.referrer).origin;
        if (ALLOWED_PARENTS.includes(refOrigin)) {
          return refOrigin;
        }
      }
    } catch {
    }

    return 'https://sig-mtm-we-webapp-adm-app-uat.azurewebsites.net';
  }

  private notifyParent(type: 'ORDER_SAVED' | 'ORDER_SUBMITTED', payload: any = {}): void {
    const parentOrigin = this.resolveParentOrigin();
    try {
      window.parent.postMessage({ type, ...payload }, parentOrigin);
    } catch (e) {
      console.error('postMessage failed to parentOrigin', parentOrigin, e);
    }
  }

  async save(){
    this.isLoading = true;

    try {
      const payload = await this.buildPayload();

      this.orderFlowService.saveOrder(payload, this.orderStatus ?? undefined).subscribe(
        (response) => {
          const orderNumber = response.data;

          this.notifyParent('ORDER_SAVED', { orderNumber });

          alert('Order saved successfully!');

          this.savedOrderNumber = orderNumber;
          this.isLoading = false;

          this.router.navigate([`/${this.countryCode}/orders`]);
        },
        (error) => {
          console.error('Error save order', error);
          alert('Error save the order. Please try again.');
          this.isLoading = false;
        },
      );
    } catch (error) {
      console.error('Error building payload:', error);
      alert('Error preparing the order data. Please try again.');
      this.isLoading = false;
    }
  }

  // Main continue method
  async onContinue() {
    this.isLoading = true;

    // Scroll to the top smoothly
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });

    switch (this.currentStep) {
      case 1:
        this.handleStep1();
        break;
      case 2:
        this.handleStep2();
        break;
      case 3:
        await this.handleStep3();
        break;
      default:
        console.error('Unknown step:', this.currentStep);
        this.isLoading = false;
    }
  }

  // Handle Step 1: Order Detail
  private handleStep1() {
    if (this.orderDetailComponent.isValid()) {
      this.currentStep = 2;
      this.enableTab(2, 'pills-step2-tab');
    } else {
      console.error('Order detail form is invalid');
      this.orderDetailComponent.markAllAsTouched();
      this.isLoading = false;
    }
  }

  // Handle Step 2: Choose Product
 private handleStep2() {
  this.isLoading = true;
  
  if (this.productChoiceComponent?.configurationForm?.valid) {
    this.productNameSelected = this.productChoiceComponent.productNameSelected;
    
    // Preservar dados de acessórios e medidas antes de fazer mudanças
    if (this.orderFlowMeasureComponent) {
      this.filledMeasurements = this.orderFlowMeasureComponent.measurements || [];
      this.filledAccessories = this.orderFlowMeasureComponent.filledAccessories || [];
    }
    
    // Check if changes were made
    const hasChanges = this.productChoiceComponent.hasChanges();
    if (hasChanges) {
      // Changes were made, proceed to addProductsToOrder
      this.isLoadingChange = true;
      this.addProductsToOrder();
    } else if (!this.productChoiceComponent.modelChoiceComponent.selectedModelId) {
      alert('Please select a model!');
      this.isLoading = false;
      return;
    } else {
      // No changes, proceed to step 3
      this.currentStep = 3;
      this.switchTab('pills-step3-tab');
      this.isLoading = false;
    }
  } else {
    // Handle invalid form
    alert('Required field is missing');
    this.productChoiceComponent?.configurationForm?.markAllAsTouched();
    this.isLoading = false;
  }
}
  // Handle Step 3: Measure
  private async handleStep3() {
    if (this.orderFlowMeasureComponent.isValid()) {
      // Emit completed data
      this.orderFlowMeasureComponent.emitCompletedData();

      this.filledMeasurements =
        this.orderFlowMeasureComponent.filledMeasurements;
      this.filledAccessories = this.orderFlowMeasureComponent.filledAccessories;

      // Build the final payload
      this.submitPayload = await this.buildPayload();

      if (this.orderDetailSummary) {
        // Update the order summary with product and model details
        this.orderDetailSummary = this.orderDetailSummary.concat(
          this.productChoiceComponent.assembleSummaryPayload(),
        );
      } else {
        this.orderDetailSummary =
          this.orderDetailComponent.assembleSummaryPayload();
      }

      // Show the summary
      this.showSummary = true;
      this.hideBtnCancel = false;
      this.hideBtnSubmit = this.orderStatus === 'Submitted' ? true : false;
      this.isLoading = false;
    } else {
      console.error('Measurements are invalid');
      this.orderFlowMeasureComponent.markAllFieldsAsTouched();
      this.isLoading = false;
    }
  }

  // Enable a specific tab based on the step
  private enableTab(step: number, tabId: string) {
    this.switchTab(tabId);
    this.isLoading = false;
  }

  // Switch to a specific tab
  public switchTab(tabId: string) {
    const tabElement = document.getElementById(tabId);
    if (tabElement) {
      const tab = new window.bootstrap.Tab(tabElement);
      tab.show();
    } else {
      console.error(`Tab element with ID '${tabId}' not found.`);
    }
  }

  // Add products to the order
  private async addProductsToOrder() {
    const selectedProductId = this.productChoiceComponent.selectedProductId;
    const selectedModelId =
      this.productChoiceComponent.modelChoiceComponent?.selectedModelId;
    const attributeOptions =
      this.productChoiceComponent.modelChoiceComponent?.attributeOptions;

    if (selectedProductId !== null && selectedModelId !== null) {
      const payload = {
        productId: selectedProductId,
        modelId: selectedModelId,
        attributeOptions: attributeOptions || [],
      };

      this.isLoadingChange = true;
      try {
        const response = await this.orderFlowService.addProductsToOrder(payload).toPromise();
        this.handleAddProductsResponse(response);
      } catch (error) {
        console.error('Error adding products to order:', error);
      } finally {
        this.isLoadingChange = false;
      }
    } else {
      console.error('Product or Model is not selected.');
      this.isLoadingChange = false;
    }
  }


  private async handleGetOrderToEditResponse(response: any) {
    if (response.data?.measure?.accessories) {
    response.data.measure.accessories.forEach((acc: any) => {
      acc.attributes?.forEach((attr: any) => {
        if (!attr.guid) {
          console.warn(`⚠️ Backend missing GUID for: ${attr.name} (ID: ${attr.attributeId})`);
        }
      });
    });
  }

    this.orderDetailComponent.orderDetailsGroups =
      response.data.orderDetails.details;
    this.orderDetailComponent.initForm();
    this.orderDetailComponent.populateForm(response.data.orderDetails.details);

    this.productChoiceComponent.data = response.data.orderProducts;
    this.productChoiceComponent.products =
      this.productChoiceComponent.sortByOrder(
        response.data.orderProducts.products,
      );
    this.productChoiceComponent.populateProductChoice(
      response.data.orderProducts,
    );

    if (response.data?.orderMeasurements?.measurements) {
      this.orderFlowMeasureComponent.measurements =
        response.data.orderMeasurements.measurements;
    }

     if (response.data?.orderMeasurements?.accessories) {
    const transformedAccessories = this.transformAccessoriesToFilledAccessories(
      response.data.orderMeasurements.accessories,
    );
    
    // APLICAR ensureAttributeGuids AQUI - Quando carrega order para edição
    this.ensureAttributeGuids(transformedAccessories);
    console.log('GUIDs ensured for accessories in edit mode:', transformedAccessories);
    
    this.orderFlowMeasureComponent.accessories = this.mergeAccessories(
      transformedAccessories,
      this.filledAccessories,
    );
    
    // NOVO: Popular os selectedValues com os dados do backend
    this.populateAccessoryValues(response.data.orderMeasurements.accessories);
  }

    this.orderFlowMeasureComponent?.measurements?.forEach(
      (measurementContainer: MeasurementContainer) => {
        measurementContainer.measures.sort((a, b) => a.order - b.order);
        this.orderFlowMeasureComponent.fetchSvg(measurementContainer);
      },
    );

    this.orderFlowMeasureComponent?.measurements?.forEach(
      (measurementContainer) => {
        measurementContainer.measures.forEach((measure) => {
          if (
            measure.numberOfColumns === undefined ||
            measure.numberOfColumns === null
          ) {
            measure.numberOfColumns = measurementContainer.numberOfColumns;
          }
          if (
            measure.selectedValue !== undefined &&
            measure.selectedValue !== null
          ) {
            this.orderFlowMeasureComponent.measurementValues[measure.id] =
              parseFloat(measure.selectedValue.toString());
          }
        });
      },
    );

     setTimeout(() => {
    // Pegar as attributeOptions diretamente do response
    const attributeOptions = response.data.orderProducts?.attributeOptions || [];
    
    // Aplicar o status em todas as medidas
    this.orderFlowMeasureComponent?.measurements?.forEach((container) => {
      this.orderFlowMeasureComponent.updateMeasuresStatus(attributeOptions, container.measures);
    });
  }, 0);

    if (this.orderDetailSummary) {
      // Update the order summary with product and model details
      this.orderDetailSummary = this.orderDetailSummary.concat(
        this.productChoiceComponent.assembleSummaryPayload(),
      );
    } else {
      this.orderDetailSummary =
        this.orderDetailComponent.assembleSummaryPayload();
    }

    this.filledMeasurements = this.orderFlowMeasureComponent.measurements;
  }

  // Handle response from adding products to order
  private handleAddProductsResponse(response: any) {
    const newMeasurements = response.data
      .measurements as MeasurementContainer[];
    const newAccessories = response.data.accessories as Accessory[];

    // Merge newMeasurements with existing filledMeasurements
    this.orderFlowMeasureComponent.measurements = this.mergeMeasurements(
      newMeasurements,
      this.filledMeasurements,
    );

    // Transform accessories to FilledAccessories
    const transformedAccessories = this.transformAccessoriesToFilledAccessories(newAccessories);
    
    // APLICAR ensureAttributeGuids AQUI - Quando carrega accessories da API
    this.ensureAttributeGuids(transformedAccessories);
    console.log('GUIDs ensured for accessories from API:', transformedAccessories);
    
    // Merge newAccessories with existing filledAccessories
    this.orderFlowMeasureComponent.accessories = this.mergeAccessories(
      transformedAccessories,
      this.filledAccessories,
    );

    this.orderFlowMeasureComponent.setGuid();
    
  

    // Existing code to fetch SVGs and set GUIDs
    this.orderFlowMeasureComponent.measurements.forEach(
      (measurementContainer: MeasurementContainer) => {
        measurementContainer.measures.sort((a, b) => a.order - b.order);
        this.orderFlowMeasureComponent.fetchSvg(measurementContainer);
        this.orderFlowMeasureComponent.updateMeasuresStatus(
          this.productChoiceComponent.modelChoiceComponent?.attributeOptions,
          measurementContainer.measures,
        );
      },
    );

    // Update measurement values in the component
    this.orderFlowMeasureComponent.measurements.forEach(
      (measurementContainer) => {
        measurementContainer.measures.forEach((measure) => {
          if (
            measure.numberOfColumns === undefined ||
            measure.numberOfColumns === null
          ) {
            measure.numberOfColumns = measurementContainer.numberOfColumns;
          }
          if (
            measure.selectedValue !== undefined &&
            measure.selectedValue !== null
          ) {
            this.orderFlowMeasureComponent.measurementValues[measure.id] =
              parseFloat(measure.selectedValue.toString());
          }
        });
      },
    );

    // Update the order summary
    if (this.orderDetailSummary) {
      this.orderDetailSummary = this.orderDetailSummary.concat(
        this.productChoiceComponent.assembleSummaryPayload(),
      );
    } else {
      this.orderDetailSummary =
        this.orderDetailComponent.assembleSummaryPayload();
    }

    // Update filledMeasurements and filledAccessories
    this.filledMeasurements = this.orderFlowMeasureComponent.measurements;
    this.filledAccessories = this.orderFlowMeasureComponent.filledAccessories;

    this.currentStep = 3;
    this.switchTab('pills-step3-tab');
    this.isLoading = false;
      this.orderFlowMeasureComponent.loadSelectedImagesFromAccessories();
  }

private populateAccessoryValues(backendAccessories: any[]) {
  backendAccessories.forEach((backendAccessory: any) => {
    // Encontrar o acessório correspondente no componente
    const componentAccessory = this.orderFlowMeasureComponent.accessories.find(
      (acc) =>
        acc.accessory.accessoryId === backendAccessory.accessoryId &&
        acc.accessory.accessoryName === backendAccessory.accessoryName
    );

    if (!componentAccessory || !backendAccessory.attributes) {
      return;
    }

    backendAccessory.attributes.forEach((backendAttribute: any) => {
      // Encontrar o atributo correspondente
      const attribute = componentAccessory.accessory.attributes.find(
        (attr: any) => attr.attributeId === backendAttribute.attributeId
      );

      if (!attribute) {
        return;
      }

      // ---- teu código original a partir daqui ----
      if (attribute.componentType === 'Text') {
        // Para campos de texto, usar selectedValue diretamente
        this.orderFlowMeasureComponent.selectedValues[attribute.guid] =
          backendAttribute.selectedValue;
        attribute.value = backendAttribute.selectedValue;
      } else if (
        attribute.componentType === 'Select' ||
        attribute.componentType === 'RadioButton' ||
        attribute.componentType === 'CheckBox'
      ) {
        // Para Select/Radio/Checkbox, encontrar qual value foi selecionado
        const selectedValue = backendAttribute.values?.find(
          (v: any) => v.selectedValue && v.selectedValue !== ''
        );

        if (selectedValue) {
          // Setar o ID do value selecionado
          this.orderFlowMeasureComponent.selectedValues[attribute.guid] =
            selectedValue.id;

          // Carregar o SVG se for Select
          if (attribute.componentType === 'Select') {
            setTimeout(() => {
              this.orderFlowMeasureComponent.getSvgContent(attribute);
            }, 100);

            // Adicionar à lista de imagens selecionadas
            const existingImageIndex =
              this.orderFlowMeasureComponent.selectedImages.findIndex(
                (img) => img.guid === attribute.guid
              );

            const imageData = {
              guid: attribute.guid,
              attributeId: attribute.attributeId,
              valueId: selectedValue.id,
              imageUrl: selectedValue.imageUrl || '',
            };

            if (existingImageIndex !== -1) {
              this.orderFlowMeasureComponent.selectedImages[existingImageIndex] =
                imageData;
            } else {
              this.orderFlowMeasureComponent.selectedImages.push(imageData);
            }
          }
        }
      }
    });
  });

  // Atualizar visibilidade após popular os valores
  this.orderFlowMeasureComponent.updateVisibility();
  this.orderFlowMeasureComponent.loadSelectedImagesFromAccessories();
}



  /**
   * Determines if the current step's form is valid.
   * @returns True if valid, else false.
   */
  isCurrentStepValid(): boolean {
    switch (this.currentStep) {
      case 1:
        return this.orderDetailComponent?.isValid() ?? false;
      case 2:
        return this.productChoiceComponent?.configurationForm?.valid ?? false;
      case 3:
        return this.orderFlowMeasureComponent?.isValid() ?? false;
      default:
        return false;
    }
  }

  private mergeMeasurements(
    newMeasurements: MeasurementContainer[],
    existingMeasurements: MeasurementContainer[],
  ): MeasurementContainer[] {
    newMeasurements.forEach((newMeasurement) => {
      const existingMeasurement = existingMeasurements.find(
        (em) => em.attributeOptionId === newMeasurement.attributeOptionId,
      );
      if (existingMeasurement) {
        newMeasurement.measures.forEach((measure) => {
          const existingMeasure = existingMeasurement.measures.find(
            (em) => em.id === measure.id,
          );
          if (existingMeasure) {
            measure.selectedValue = existingMeasure.selectedValue;
          }
        });
      }
    });
    return newMeasurements;
  }

  private mergeAccessories(
    newAccessories: FilledAccessory[],
    existingAccessories: FilledAccessory[],
  ): FilledAccessory[] {
    newAccessories.forEach((newAccessory) => {
      const existingAccessory = existingAccessories.find(
        (ea) => ea.accessory.accessoryId === newAccessory.accessory.accessoryId,
      );
      if (existingAccessory) {
        // Merge selectedAttributes
        newAccessory.selectedAttributes.forEach((attr) => {
          const existingAttr = existingAccessory.selectedAttributes.find(
            (ea) => ea.attributeId === attr.attributeId,
          );
          if (existingAttr) {
            attr.selectedValue = existingAttr.selectedValue;
          }
        });
        // Merge selectedImage if necessary
        if (existingAccessory.selectedImage) {
        
          newAccessory.selectedImage = existingAccessory.selectedImage;
        }
      }
    });
    return newAccessories;
  }

  private transformAccessoriesToFilledAccessories(
    accessories: Accessory[],
  ): FilledAccessory[] {
    return accessories.map((accessory) => {
      return {
        accessory: accessory,
        selectedAttributes: [],
        selectedImage: null,
      };
    });
  }

  // Build the final payload for submission
 private async buildPayload() {
  // Garantir que temos os dados de medidas e acessórios mais recentes
  if (this.orderFlowMeasureComponent) {
    this.orderFlowMeasureComponent.emitCompletedData();
    this.filledMeasurements = this.orderFlowMeasureComponent.filledMeasurements || [];
    this.filledAccessories = this.orderFlowMeasureComponent.filledAccessories || [];
  }
  const touchedFields = [
    ...(this.orderDetailComponent?.touchedFields || []),
    ...(this.productChoiceComponent?.modelChoiceComponent?.touchedFields || [])
  ];
  const payload: any = {
    orderNumber: this.savedOrderNumber,
    baseOrderNumber: this.baseOrderNumber,
    orderDetails: this.orderDetailComponent ? this.orderDetailComponent.assemblePayload() : {},
    productChoice: this.productChoiceComponent ? this.productChoiceComponent.buildProductChoicePayload() : {},
    attachments: this.orderFlowOrderSummaryComponent?.attachments || [],
    clinicId: this.authService.getSelectedClinic()?.id ?? undefined,
    clinicName: this.authService.getSelectedClinic()?.name ?? undefined,
    measure: {
      measurements: [],
      accessories: [],
    },
    touchedFields:touchedFields,
    comments: this.orderFlowOrderSummaryComponent?.comments ?? this.comments ?? null,
  };

  // Populate accessories - usar filledAccessories se orderFlowMeasureComponent não estiver disponível
  const accessoriesToProcess = this.orderFlowMeasureComponent?.filledAccessories || this.filledAccessories;
  
  if (accessoriesToProcess && accessoriesToProcess.length > 0) {
    try {
      // Se orderFlowMeasureComponent estiver disponível, usar seu método
      if (this.orderFlowMeasureComponent && this.orderFlowMeasureComponent.mapFilledAccessoriesToAccessoriesData) {
        const accessoriesData = await this.orderFlowMeasureComponent.mapFilledAccessoriesToAccessoriesData(
          accessoriesToProcess
        );
        payload.measure.accessories = accessoriesData.accessories || [];
      } else {
        // Fallback: mapear manualmente se o componente não estiver disponível
        payload.measure.accessories = this.mapAccessoriesManually(accessoriesToProcess);
      }
    } catch (error) {
      console.error('Error mapping filled accessories:', error);
      payload.measure.accessories = [];
    }
  }

  // Build measurements - usar filledMeasurements se o componente não estiver disponível
  const measurementsToProcess = this.orderFlowMeasureComponent?.measurements || this.filledMeasurements;
  
  if (measurementsToProcess && measurementsToProcess.length > 0) {
    measurementsToProcess.forEach((measurement: any) => {
      const measurementPayload: any = {
        attributeOptionId: measurement.attributeOptionId,
        attributeOptionName: measurement.attributeOptionName,
        measures: [],
      };

      if (measurement.measures && measurement.measures.length > 0) {
        measurement.measures.forEach((measure: any) => {
          // Verificar se temos o valor da medida
          let measureValue = '0';
          
          if (this.orderFlowMeasureComponent?.measurementValues?.[measure.id]) {
            measureValue = this.orderFlowMeasureComponent.measurementValues[measure.id].toString();
          } else if (measure.selectedValue !== undefined && measure.selectedValue !== null) {
            measureValue = measure.selectedValue.toString();
          }

          const measurePayload = {
            baseMeasurementId: measure.id,
            baseMeasurementName: measure.name,
            measure: measureValue,
          };
          measurementPayload.measures.push(measurePayload);
        });
      }

      payload.measure.measurements.push(measurementPayload);
    });
  }

  return payload;
}

private mapAccessoriesManually(filledAccessories: FilledAccessory[]): any[] {
  return filledAccessories.map(filledAccessory => {
    const accessoryData: any = {
      accessoryId: filledAccessory.accessory.accessoryId,
      accessoryName: filledAccessory.accessory.accessoryName,
      attributes: []
    };

    // Mapear atributos selecionados
    if (filledAccessory.selectedAttributes && filledAccessory.selectedAttributes.length > 0) {
      accessoryData.attributes = filledAccessory.selectedAttributes.map(attr => ({
        attributeId: attr.attributeId,
        attributeName: attr.name || '',
        selectedValue: attr.selectedValue || ''
      }));
    }

    // Adicionar imagem selecionada se houver
    if (filledAccessory.selectedImage) {
   
      accessoryData.selectedImage = filledAccessory.selectedImage;
    }

    return accessoryData;
  });
}

  // Submit the order — therapist: button says "Submit" but sends ReadyToSubmit behind the scenes
  async submitOrder() {
    if (this.authService.isTherapist()) {
      this.submitAsReadyToSubmit(await this.buildPayload());
      this.clearUnsavedChanges();
      return;
    }

    this.sendPayload(await this.buildPayload());

    if (!this.isQuote)
      this.clearUnsavedChanges();
  }

  private submitAsReadyToSubmit(payload: any): void {
    this.hideBtnSubmit = true;
    this.isLoading = true;

    this.orderFlowService.saveOrder(payload, 'ReadyToSubmit').subscribe({
      next: (response) => {
        this.isLoading = false;
        this.notifyParent('ORDER_SAVED', { orderNumber: response.data });
        this.router.navigate([`/${this.countryCode}/orders`]);
      },
      error: () => {
        this.isLoading = false;
        this.hideBtnSubmit = false;
        alert('Error submitting the order. Please try again.');
      },
    });
  }

  // Send the final payload to the server
  private sendPayload(payload: any) {
    this.hideBtnSubmit = true; 
    
    const needToQuote = payload.orderDetails?.find(
      d => d.orderDetailsSourceValue?.includes("Yes")
    );

      if (needToQuote) {
      const approvalEntry = payload.orderDetails?.find(
        d => d.orderDetailsSourceId === needToQuote.orderDetailsSourceId &&
            d.orderDetailsSourceValue?.includes("Yes")
      );

     this.isQuote = true;
    }
    
    this.isLoading = true;
    this.orderFlowService.submitOrder(payload).subscribe(
      async (response) => {
        this.quoteOrderNumber = response.data;
        const orderNumber = this.quoteOrderNumber;
        if (!this.isQuote) {
          this.router.navigate(['/order-submitted']);
        }

        if(needToQuote) {
         await this.quotePrice('dealer'); 
         await this.quotePrice('msrp'); 
        }

        this.isLoading = false;
        this.notifyParent('ORDER_SUBMITTED', { orderNumber });
      },
      (error) => {
        alert('Error creating the order. Please try again.');
        this.isLoading = false;
        this.hideBtnSubmit = false;
      },
    );

  }
  
  async quotePrice(type: 'dealer' | 'msrp') {
    this.selectedQuoteType = type; 
    const payload = await this.buildPayload();

    this.hideBtnSubmit = true;
    this.isLoading = true;

    const needToQuote = payload.orderDetails?.find(
      (d: any) => d.orderDetailsSourceValue?.includes('Yes')
    );
    if (needToQuote) {
      const approvalEntry = payload.orderDetails?.find(
        (d: any) =>
          d.orderDetailsSourceId === needToQuote.orderDetailsSourceId &&
          d.orderDetailsSourceValue?.includes('Yes')
      );
      this.isQuote = true;
    } else {
      this.isQuote = true;
    }

    this.orderFlowService.quoteOrder(payload,this.quoteOrderNumber, type).subscribe(
      (res) => {
        try {
          const blob = (res as any).body ?? res;
          const contentDisposition =
            (res as any).headers?.get?.('content-disposition') ?? '';

          const rfc5987 = /filename\*=UTF-8''([^;]+)/i.exec(contentDisposition);
          const basic = /filename="?([^"]+)"?/i.exec(contentDisposition);
          const fileName =
            rfc5987?.[1]
              ? decodeURIComponent(rfc5987[1])
              : basic?.[1] ?? `Quote_${type}_${this.quoteOrderNumber ?? 'download'}.pdf`;

          const url = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
          const a = document.createElement('a');
          a.href = url;
          a.download = fileName;
          document.body.appendChild(a);
          a.click();
          a.remove();
          URL.revokeObjectURL(url);
        } catch (e) {
          console.error('Failed to process quote PDF', e);
          this.hideBtnSubmit = true;
        } finally {
          this.router.navigate(['/en-us/orders']);
          this.isLoading = false;
          this.hideBtnSubmit = true;
        }
      },
      (error) => {
        console.error(`Error creating the ${type} quote`, error);
        alert('Error creating the order. Please try again.');
        this.isLoading = false;
        this.hideBtnSubmit = true;
      }
    );

    this.clearUnsavedChanges();
  }

  // Cancel the current step or reset the flow
  onCancel() {
    // If we're currently showing the summary, revert to step 3
    if (this.showSummary) {
      this.showSummary = false;
      this.currentStep = 3;
      setTimeout(() => {
        this.switchTab('pills-step3-tab');
      }, 500);
      return;
    }

    // Otherwise, if not on the summary, handle as per your original logic
    if (this.currentStep > 1) {
      this.currentStep--;
      this.switchTab(this.getTabIdForStep(this.currentStep));
    } else {
      // If needed, implement logic here for when you are on step 1.
      // For example, navigate back or reset the order flow.
      this.router.navigate([`/${this.countryCode}/orders`]);

      this.resetOrderFlow();
    }
  }

  // Get the tab ID based on the step number
  private getTabIdForStep(step: number): string {
    switch (step) {
      case 1:
        return 'pills-step1-tab';
      case 2:
        return 'pills-step2-tab';
      case 3:
        return 'pills-step3-tab';
      default:
        return 'pills-step1-tab';
    }
  }

  onTabClick(step: number, tabId: string) {
    this.currentStep = step;
    this.switchTab(tabId);
  }

  // Reset the entire order flow to its initial state
  private resetOrderFlow() {
    this.currentStep = 1;
    this.productChoiceComponent.selectedProductId = null;
    this.orderFlowMeasureComponent.measurements = [];
    this.orderFlowMeasureComponent.accessories = [];
    this.orderDetailComponent.resetForm(); // Reset the order detail form
    this.switchTab('pills-step1-tab');
  }
}